// src/components/SharedComponents/CreditCardForm.js
// @flow

/* eslint-disable react/no-multi-comp */
import React, { useState } from 'react';
import * as R from 'ramda';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import classNames from 'classnames';

import { useStore } from '../../store';
import { log } from '../../utils/jsUtils';
import SPBackend from '../../services/SPBackend';

import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import OutlinedInput from '@mui/material/OutlinedInput';
import Button from '@mui/material/Button';

import StripeElementWrapper from '../Billing/StripeElementWrapper';

// Old style modules:
import signupStyles from '../PublicCpts/Styles/Signup.Style';
import billingStyles from '../Billing/Styles/BillingContent.Style';

// MUI 5 combined styles approach:
import combineStyles, { createClasses } from '../../utils/stylesUtils';
import creditCardFormStyles from './Styles/CreditCardForm.Style';

// MUI 5 style creation
import { useTheme } from '@mui/material/styles';

// Build a single combined style object:
const combinedStyles = combineStyles(
  signupStyles,
  creditCardFormStyles,
  billingStyles
);

// Set up Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

type Props = {
  getPaymentMethods: Function,
  onPaymentMethodSelected: Function,
  onCancelClicked?: Function
};

/**
 * The actual form sub-component that uses stripe's elements
 */
function CreditCardInputForm(props: Props) {
  const { getPaymentMethods, onPaymentMethodSelected, onCancelClicked } = props;

  const [store, dispatch] = useStore();
  const { user } = store;

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [chargeError, setChargeError] = useState('');

  const stripe = useStripe();
  const elements = useElements();

  const theme = useTheme();
  const classes = createClasses(combinedStyles, theme);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setPaymentLoading(true);
    log('submit payment!', ev);

    try {
      const cardElement = elements.getElement('card');
      log('[handleSubmit] elements(cardNumber): ', cardElement);

      const pmResult = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName
        }
      });
      const { paymentMethod, error: pmError } = pmResult;
      if (pmError) {
        log('Stripe paymentMethod error: ', pmError);
        if (pmError.code === 'parameter_invalid_empty') {
          throw Error('All fields are required.');
        }
        throw Error(pmError.message);
      }

      SPBackend.updatePaymentMethod(paymentMethod, user)
        .then((response) => {
          onPaymentMethodSelected(paymentMethod);
          setChargeError('');
          log('charge response: ', response);
          setPaymentLoading(false);
          dispatch({
            type: 'ACTION_SET_SNACK_MSG',
            snackMessage: {
              message: 'Payment details updated!',
              level: 'success',
              duration: 3000
            }
          });
          getPaymentMethods();
        })
        .catch((error) => {
          log('[handleSubmit] charge error.response: ', error.response);
          setPaymentLoading(false);
          const data = error.response?.data || 'Card processing failed. Please try again.';
          setChargeError(data);
        });
    } catch (e) {
      log('[handleSubmit] e.name: ', e.name);
      if (e.name === 'IntegrationError') {
        setChargeError('Missing card information');
      } else {
        log('[handleSubmit] error caught: ', e.name, e);
        setChargeError(e.message);
      }
      setPaymentLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container>
        <Grid item xs={12}>
          <Typography className={classes.formLabel} variant="body1">
            {'Cardholder full name'}
          </Typography>
          <FormControl
            className={classNames(classes.newInputForm)}
            variant="outlined"
          >
            <OutlinedInput
              className={classNames(classes.newFormElt, classes.checkoutInputForm)}
              id="cardholderName"
              margin="dense"
              name="cardholderName"
              onChange={(event) => setCardholderName(event.target.value)}
              placeholder="Cardholder full name"
              type="text"
              value={cardholderName}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.formLabel} variant="body1">
            {'Card information'}
          </Typography>
          <StripeElementWrapper
            component={CardElement}
            id="card-elt-id"
            label=""
          />
        </Grid>

        {chargeError !== '' && (
          <Grid className={classes.errorLabel} item xs={12}>
            {chargeError}
          </Grid>
        )}

        <div className={classes.actionsCtr}>
          {!R.isNil(onCancelClicked) && (
            <Button
              color="primary"
              onClick={(event) => {
                event.preventDefault();
                onCancelClicked();
              }}
              type="button"
            >
              {'Cancel'}
            </Button>
          )}
          <Button
            color="primary"
            disabled={paymentLoading}
            style={{ marginTop: 16 }}
            type="submit"
            variant="contained"
          >
            {!paymentLoading ? 'Save' : 'Loading'}
          </Button>
        </div>
      </Grid>
    </form>
  );
}

/**
 * The top-level component that wraps the form in <Elements>
 */
function CreditCardForm(props: Props) {
  const { onPaymentMethodSelected, onCancelClicked } = props;
  const [store] = useStore();
  const { user } = store;

  const getPaymentMethods = () => {
    SPBackend.getPaymentMethods(user)
      .then((response) => {
        log('[CreditCardForm.getPaymentMethods] response: ', response);
        const { paymentMethods, defaultPaymentMethod } = response.data;
        // ...
      })
      .catch((e) => {
        log('[CreditCardForm.getPaymentMethods] error: ', e);
      });
  };

  return (
    <Elements stripe={stripePromise}>
      <CreditCardInputForm
        getPaymentMethods={getPaymentMethods}
        onCancelClicked={onCancelClicked}
        onPaymentMethodSelected={onPaymentMethodSelected}
      />
    </Elements>
  );
}

export default CreditCardForm;